import Box from '@quintoandar/cozy-core/Box';
import Card from '@quintoandar/cozy-core/Card';
import CircularProgress from '@quintoandar/cozy-core/CircularProgress';
import Progress from '@quintoandar/cozy-core/Progress';
import Typography from '@quintoandar/cozy-core/Typography';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { StatusCircle } from './StatusCircle.js';
import messages from './messages.js';
import { useRetrieveSignedUrl } from './useRetrieveSignedUrl.js';
import { useUploadS3File } from './useUploadS3File.js';
import { UPLOAD_STATUS } from './constants.js';

export const S3uploader = ({
  currentStatus,
  file,
  onError,
  onUploadSuccess,
  houseId,
}) => {
  const intl = useIntl();
  const [progress, setProgress] = useState(0);
  const onProgressChange = newProgress => setProgress(newProgress);
  const {
    isLoading: isRetrievingSignedUrl,
    mutate: retrieveSignedUrl,
  } = useRetrieveSignedUrl(file ? file.name : null, houseId);
  const {
    isLoading: isUploadingVideo,
    isSuccess: uploadedVideo,
    mutate: uploadVideo,
  } = useUploadS3File(onProgressChange);

  const isError = currentStatus === UPLOAD_STATUS.ERROR_UPLOADING;

  const hasUploaded = uploadedVideo ||
    currentStatus === UPLOAD_STATUS.SUCCESS ||
    currentStatus === UPLOAD_STATUS.POLLING ||
    currentStatus === UPLOAD_STATUS.ERROR_POLLING;

  useEffect(() => {
    if (currentStatus === UPLOAD_STATUS.UPLOADING) {
      const fileName = file.name;
      retrieveSignedUrl({ fileName, houseId }, {
        onError,
        onSuccess: (signedUrl) => {
          uploadVideo({ signedUrl, file }, {
            onError,
            onSuccess: () => onUploadSuccess(),
          });
        },
      });
    }
  }, [currentStatus]);

  if (currentStatus === UPLOAD_STATUS.UNSELECTED_FILE) {
    return null;
  }

  const getTitleMessage = () => {
    if (hasUploaded) {
      return intl.formatMessage(messages.videoUploadedSuccessfully);
    }
    if (isError) {
      return intl.formatMessage(messages.errorInUpload);
    }

    return intl.formatMessage(messages.uploadingVideo);
  };

  const isLoading = isRetrievingSignedUrl || isUploadingVideo;

  return (
    <Card>
      <Box display="flex" alignItems="center" width="100%" minHeight="40px">
        <Box
          height="100%"
          display="flex"
          alignItems="flex-start"
          justifyContent="start"
        >
          <Box>{isLoading && !isError && <CircularProgress size={25} />}</Box>
          {(hasUploaded) && <StatusCircle size={20} color="#8ccf59" />}
          {isError && <StatusCircle size={20} color="#c13c37" />}
        </Box>
        <Box ml={3} width="100%" minHeight="100%">
          <Box display="flex" alignItems="flex-start">
            <Typography align="left" level="bodyStrong">
              {getTitleMessage()}
            </Typography>
          </Box>
          {isLoading && (
            <Box mt={1} width="80%">
              <Progress color="primary" value={progress} />
            </Box>
          )}
          {isError && (
            <Box mt={1} width="80%">
              <Typography level="caption">
                <FormattedMessage {...messages.errorProcessingVideo} />
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Card>
  );
};
S3uploader.propTypes = {
  currentStatus: PropTypes.string.isRequired,
  file: PropTypes.object.isRequired,
  onError: PropTypes.func.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
  houseId: PropTypes.string.isRequired,
};
